import React from "react";
import { View, Text, StyleSheet, Dimensions } from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ChildStackParamList } from "../../navigation/types";
import useKeydown from "../../hooks/useKeydown";
import { Mascot } from "../../components/Mascot";
import { useAuthStore } from "../../state/authStore";
import { colors, fontFamily } from "../../theme";

type Props = NativeStackScreenProps<ChildStackParamList, "BreakTime">;

const { width, height } = Dimensions.get("window");
const scale = (size: number) => (width / 1920) * size;

// Soft "Mommy says it's break time" screen. The framing is intentional —
// the child sees the mascot's voice, not a "your parent is watching you"
// surveillance message. Identical structure to TimesUp, different copy.
export default function BreakTimeScreen({ navigation }: Props) {
  const { activeChild } = useAuthStore();

  // "Go home" = the kid's home (ChildHome), not the "Who's watching?"
  // profile picker. ChildHome needs a childId; activeChild is seeded when
  // the kid is selected on ProfileSelect, so it's reliably present here.
  // Fall back to the picker only if we somehow lost the active child.
  const goHome = () => {
    if (activeChild) {
      navigation.replace("ChildHome", { childId: activeChild.id });
    } else {
      navigation.replace("ProfileSelect");
    }
  };

  useKeydown({
    isActive: true,
    ok: goHome,
    back: goHome,
  });

  return (
    <View style={styles.container}>
      <View style={styles.mascotWrap}>
        <Mascot mood="happy" color={colors.primary} size={scale(280)} bob />
      </View>

      <Text style={styles.title}>Time for a break!</Text>
      <Text style={styles.subtitle}>
        See you next time — let&apos;s go play!
      </Text>

      <View style={styles.hintPill}>
        <Text style={styles.hintText}>Press OK to go home</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    // Explicit min height pinned to the viewport. On engine-rn-web
    // (Tizen/webOS Chromium 76) the flex chain from #root collapses, so
    // `flex: 1` alone only painted the warm background over the content's
    // intrinsic height — leaving the bottom ~third on the page default.
    // Same fix as the ProfileSelect QR overlay.
    minHeight: height,
    backgroundColor: colors.bgWarm,
    alignItems: "center",
    justifyContent: "center",
    padding: scale(80),
  },
  mascotWrap: {
    marginBottom: scale(40),
  },
  title: {
    fontFamily,
    fontSize: scale(80),
    fontWeight: "900",
    color: colors.ink,
    letterSpacing: -1.2,
    marginBottom: scale(20),
  },
  subtitle: {
    fontFamily,
    fontSize: scale(32),
    fontWeight: "700",
    color: colors.ink2,
    textAlign: "center",
    maxWidth: scale(900),
    marginBottom: scale(48),
  },
  hintPill: {
    paddingHorizontal: scale(28),
    paddingVertical: scale(14),
    borderRadius: 999,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.line,
  },
  hintText: {
    fontFamily,
    fontSize: scale(22),
    fontWeight: "700",
    color: colors.ink2,
  },
});
